import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api } from '../../../lib/api-client';
import { WS_EVENTS } from '../../../lib/websocket/events';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';

import type { PlaybookTransitionProposalWire } from '../../../lib/api-client';
import type { PlaybookTransitionProposedPayload } from '../../../lib/websocket/events';

/**
 * S7 PR3 (S6 Condução Governada, Q-J6) — chave de query parametrizada por
 * conversa (molde `PROPOSALS_QUERY_KEY` de `use-my-proposals.ts`, adaptado:
 * lá a lista é global-do-atendente; aqui é por-conversa).
 */
export function playbookTransitionsQueryKey(
  conversationId: string,
): readonly [string, string, string] {
  return ['playbook-transitions', conversationId, 'awaiting_confirmation'] as const;
}

/**
 * usePlaybookTransitions — S7 PR3 (§6.1 spec, molde `use-my-proposals.ts`).
 *
 * Lista propostas `awaiting_confirmation` da conversa em foco + invalida via
 * WS `PLAYBOOK_TRANSITION_PROPOSED` (escopado por `conversationId`). Devolve
 * a proposta pendente MAIS RECENTE (o backend só permite uma proposta viva
 * por vez — `list` já ordena `proposedAt desc`) ou `null`.
 *
 * `enabled` gateia o FETCH (não só o render) — o caller passa
 * `useCapability('conversation-lifecycle-strict') && conversationId !== undefined`.
 */
export function usePlaybookTransitions(
  conversationId: string | undefined,
  enabled: boolean,
): {
  proposal: PlaybookTransitionProposalWire | null;
  isLoading: boolean;
} {
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocket();
  const isEnabled = enabled && conversationId !== undefined;

  const q = useQuery({
    queryKey: playbookTransitionsQueryKey(conversationId ?? '__none__'),
    queryFn: () =>
      api.listPlaybookTransitions({
        conversationId: conversationId!,
        status: 'awaiting_confirmation',
      }),
    enabled: isEnabled,
    staleTime: 15_000,
  });

  useEffect(() => {
    if (!isEnabled || conversationId === undefined) return;
    const unsub = subscribe<PlaybookTransitionProposedPayload>(
      WS_EVENTS.PLAYBOOK_TRANSITION_PROPOSED,
      (envelope) => {
        if (envelope.data.conversationId !== conversationId) return;
        void queryClient.invalidateQueries({
          queryKey: playbookTransitionsQueryKey(conversationId),
        });
      },
    );
    return unsub;
  }, [isEnabled, conversationId, queryClient, subscribe]);

  const items = q.data?.items ?? [];
  return {
    proposal: items[0] ?? null,
    isLoading: q.isLoading,
  };
}
